import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Document Metadata API service (Enhanced with confidence support)
 */
export const DocumentMetadataApi = {
  /**
   * Get document metadata
   * @param {string|number} documentId - Document ID
   * @returns {Promise<Response>} Document metadata response
   */
  getDocumentMetadata: async (documentId) => {
    console.log('DocumentMetadataApi: Getting metadata for document', documentId);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/metadata`, {
      method: 'GET',
    });
  },

  /**
   * ✅ REVERTED: Add document metadata (POST doesn't need ID in payload)
   * @param {Object} data - Metadata data containing documentId, metadataDefinitionId, value
   * @param {Function} invalidateCache - Cache invalidation function
   * @returns {Promise<Response>} Add metadata response
   */
  addDocumentMetadata: async (data, invalidateCache) => {
    console.log('DocumentMetadataApi: Adding metadata:', data);
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/metadata`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data), // ✅ POST doesn't need ID - server generates it
    });
    
    // Invalidate related cache entries
    if (response.ok && invalidateCache) {
      invalidateCache('document-metadata');
    }
    
    return response;
  },

  /**
   * Create document metadata with confidence score
   * @param {Object} data - Metadata data with confidence
   * @returns {Promise<Response>} Add metadata response
   */
  createDocumentMetadataWithConfidence: async (data) => {
    console.log('DocumentMetadataApi: Creating metadata with confidence:', data);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/metadata`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * ✅ UPDATED: Update document metadata with ID and confidence included in request body
   * @param {string|number} metadataId - Metadata ID
   * @param {Object} data - Updated metadata data (should contain documentId, metadataDefinitionId, value, confidence)
   * @param {Function} invalidateCache - Cache invalidation function (optional)
   * @returns {Promise<Response>} Update response
   */
  updateDocumentMetadata: async (metadataId, data, invalidateCache = null) => {
    console.log('DocumentMetadataApi: Updating metadata', metadataId, 'with data:', data);
    
    // ✅ UPDATED: Include the metadata ID and confidence in the request body payload
    const payload = {
      id: metadataId,                    // ✅ Include the metadata ID in the payload
      documentId: data.documentId,
      metadataDefinitionId: data.metadataDefinitionId,
      value: data.value,
      confidence: data.confidence || 100  // ✅ ADD: Include confidence level (default to 100)
    };
    
    console.log('DocumentMetadataApi: Sending PUT payload with ID and confidence:', payload);
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/metadata/${metadataId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload), // ✅ Send payload with ID and confidence included
    });
    
    console.log('DocumentMetadataApi: Update response:', response.status, response.ok);
    
    // Invalidate related cache entries
    if (response.ok && invalidateCache) {
      invalidateCache('document-metadata');
    }
    
    return response;
  },

  /**
   * Update document metadata with confidence score
   * @param {string|number} metadataId - Metadata ID
   * @param {Object} data - Updated metadata data with confidence
   * @returns {Promise<Response>} Update response
   */
  updateDocumentMetadataWithConfidence: async (metadataId, data) => {
    console.log('DocumentMetadataApi: Updating metadata with confidence:', metadataId, data);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/metadata/${metadataId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete document metadata (invalidates cache)
   * @param {string|number} metadataId - Metadata ID
   * @param {Function} invalidateCache - Cache invalidation function
   * @returns {Promise<Response>} Delete response
   */
  deleteDocumentMetadata: async (metadataId, invalidateCache) => {
    console.log('DocumentMetadataApi: Deleting metadata:', metadataId);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/metadata/${metadataId}`, {
      method: 'DELETE',
    });
    
    // Invalidate related cache entries
    if (response.ok && invalidateCache) {
      invalidateCache('document-metadata');
    }
    
    return response;
  },

  /**
   * Delete all document metadata
   * @param {string|number} documentId - Document ID
   * @returns {Promise<Response>} Delete response
   */
  deleteAllDocumentMetadata: async (documentId) => {
    console.log('DocumentMetadataApi: Deleting all metadata for document:', documentId);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/metadata`, {
      method: 'DELETE',
    });
  }
};